import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, Type } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { RAD_ACTIVE, RadActiveState } from 'app/rad-ui/active/active';
import { RadSidePanelComponent } from 'app/rad-ui/side-panel/side-panel.component';
import { filter, from, map, merge, Subject, switchMap } from 'rxjs';
import { PREVIEW_NAVIGATOR, PreviewService } from './preview.service';
import { PREVIEW_VIEWS } from './preview.views';

interface PreviewLevel {
    key: string;
    component: Type<unknown>;
    inputs: Record<string, string>;
    title: string;
}

// One preview on the stack. The views under the top one are hidden and kept, and told through
// RAD_ACTIVE that they are not showing, so they hold their polling until they are shown again.
@Component({
    selector: 'key-rad-preview-level',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgComponentOutlet],
    providers: [{ provide: RAD_ACTIVE, useExisting: forwardRef(() => KeyRadPreviewLevelComponent) }],
    host: {
        '[hidden]': '!active()',
    },
    template: `
        <ng-container *ngComponentOutlet="component(); inputs: inputs()" />
    `,
    styles: `
        :host {
            display: block;

            &[hidden] {
                display: none;
            }
        }
    `,
})
export class KeyRadPreviewLevelComponent implements RadActiveState {
    readonly component = input.required<Type<unknown>>();
    readonly inputs = input.required<Record<string, string>>();
    readonly active = input.required<boolean>();
}

// The app's one preview panel, showing the last preview on the stack. Placed once in the app shell.
@Component({
    selector: 'key-rad-preview-host',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [KeyRadPreviewLevelComponent, RadSidePanelComponent, TranslateModule],
    providers: [{
        provide: PREVIEW_NAVIGATOR,
        useFactory: () => {
            const previews = inject(PreviewService);
            return { open: target => previews.push(target) };
        },
    }],
    template: `
        <rad-side-panel
            modal
            size="md"
            [title]="title() | translate"
            [showBack]="previews.stack().length > 1"
            [open]="open()"
            (openChange)="setOpen($event)"
            (back)="previews.back()"
            (closed)="clearLevels()"
        >
            @for (level of levels(); track level.key; let last = $last) {
                <key-rad-preview-level [component]="level.component" [inputs]="level.inputs" [active]="last" />
            }
        </rad-side-panel>
    `,
})
export class KeyRadPreviewHostComponent {

    protected readonly previews = inject(PreviewService);

    protected readonly open = computed(() => this.previews.stack().length > 0);

    private readonly cleared = new Subject<PreviewLevel[]>();

    // A level keeps its key while the levels under it stay the same, so pushing and popping leave
    // them alive. A closed panel keeps its levels while it slides out, and destroys them once the
    // slide ends.
    protected readonly levels = toSignal(merge(
        toObservable(this.previews.stack).pipe(
            filter(stack => stack.length > 0),
            switchMap(stack => from(Promise.all(stack.map(target => PREVIEW_VIEWS[target.kind].loadComponent()))).pipe(
                map(components => stack.map((target, index): PreviewLevel => ({
                    key: `${index}/${target.kind}/${JSON.stringify(target.params)}`,
                    component: components[index],
                    inputs: target.params,
                    title: PREVIEW_VIEWS[target.kind].title,
                }))),
            )),
        ),
        this.cleared,
    ), { initialValue: [] as PreviewLevel[] });

    protected readonly title = computed(() => this.levels().at(-1)?.title ?? '');

    protected clearLevels(): void {
        this.cleared.next([]);
    }

    protected setOpen(open: boolean): void {
        if (!open) {
            this.previews.close();
        }
    }
}
